/**
 * Image Cache Utilities for PWA
 * Provides efficient caching of breakdown images using IndexedDB
 */

const DB_NAME = 'RNR_IMAGE_CACHE';
const DB_VERSION = 1;
const STORE_NAME = 'images';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

class ImageCacheManager {
  constructor() {
    this.db = null;
    this.dbPromise = this.initDB();
  }

  /**
   * Initialize IndexedDB database
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
          store.createIndex('breakdownId', 'breakdownId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Generate cache key for breakdown images
   */
  generateCacheKey(breakdownId) {
    return `breakdown_${breakdownId}_images`;
  }

  /**
   * Check if cached data exists and is still valid
   */
  async isCacheValid(breakdownId) {
    try {
      await this.dbPromise;
      const cacheKey = this.generateCacheKey(breakdownId);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(cacheKey);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result;
          if (!result) {
            resolve(false);
            return;
          }
          
          const now = Date.now();
          const cacheAge = now - result.timestamp;
          const isValid = cacheAge < CACHE_DURATION;
          
          console.log(`Cache check for breakdown ${breakdownId}: ${isValid ? 'VALID' : 'EXPIRED'} (age: ${Math.round(cacheAge / 1000 / 60)} minutes)`);
          resolve(isValid);
        };
      });
    } catch (error) {
      console.error('Error checking cache validity:', error);
      return false;
    }
  }

  /**
   * Get cached images for a breakdown
   */
  async getCachedImages(breakdownId) {
    try {
      await this.dbPromise;
      const cacheKey = this.generateCacheKey(breakdownId);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(cacheKey);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            console.log(`Retrieved ${result.images.length} cached images for breakdown ${breakdownId}`);
            resolve({
              images: result.images,
              imageIds: result.imageIds,
              timestamp: result.timestamp
            });
          } else {
            resolve(null);
          }
        };
      });
    } catch (error) {
      console.error('Error getting cached images:', error);
      return null;
    }
  }

  /**
   * Cache images for a breakdown
   */
  async cacheImages(breakdownId, images, imageIds) {
    try {
      await this.dbPromise;
      const cacheKey = this.generateCacheKey(breakdownId);
      
      const cacheData = {
        cacheKey,
        breakdownId,
        images,
        imageIds,
        timestamp: Date.now()
      };
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(cacheData);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          console.log(`Cached ${images.length} images for breakdown ${breakdownId}`);
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error caching images:', error);
      return false;
    }
  }

  /**
   * Compare image IDs to detect if new images have been uploaded
   */
  compareImageIds(cachedIds, freshIds) {
    if (!cachedIds || !freshIds) return false;
    
    // Check if arrays have different lengths
    if (cachedIds.length !== freshIds.length) {
      return false;
    }
    
    // Sort and compare arrays
    const sortedCached = [...cachedIds].sort((a, b) => a.id - b.id);
    const sortedFresh = [...freshIds].sort((a, b) => a.id - b.id);
    
    for (let i = 0; i < sortedCached.length; i++) {
      if (sortedCached[i].id !== sortedFresh[i].id) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Clear expired cache entries
   */
  async clearExpiredCache() {
    try {
      await this.dbPromise;
      const now = Date.now();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('timestamp');
        const request = index.openCursor();
        
        let deletedCount = 0;
        
        request.onerror = () => reject(request.error);
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const data = cursor.value;
            const age = now - data.timestamp;
            
            if (age > CACHE_DURATION) {
              cursor.delete();
              deletedCount++;
            }
            cursor.continue();
          } else {
            console.log(`Cleared ${deletedCount} expired cache entries`);
            resolve(deletedCount);
          }
        };
      });
    } catch (error) {
      console.error('Error clearing expired cache:', error);
      return 0;
    }
  }

  /**
   * Clear all cache data
   */
  async clearAllCache() {
    try {
      await this.dbPromise;
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          console.log('All image cache cleared');
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error clearing all cache:', error);
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    try {
      await this.dbPromise;
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const countRequest = store.count();
        
        countRequest.onerror = () => reject(countRequest.error);
        countRequest.onsuccess = () => {
          const count = countRequest.result;
          
          // Calculate total cache size (approximate)
          const getAllRequest = store.getAll();
          getAllRequest.onsuccess = () => {
            const allData = getAllRequest.result;
            let totalSize = 0;
            let oldestEntry = Date.now();
            
            allData.forEach(entry => {
              // Rough estimate of entry size
              totalSize += JSON.stringify(entry).length;
              if (entry.timestamp < oldestEntry) {
                oldestEntry = entry.timestamp;
              }
            });
            
            resolve({
              entryCount: count,
              approximateSize: totalSize,
              oldestEntry: oldestEntry === Date.now() ? null : new Date(oldestEntry)
            });
          };
        };
      });
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return null;
    }
  }
}

// Create singleton instance
const imageCacheManager = new ImageCacheManager();

export default imageCacheManager;

// Helper functions for easier usage
export const getCachedImages = (breakdownId) => imageCacheManager.getCachedImages(breakdownId);
export const cacheImages = (breakdownId, images, imageIds) => imageCacheManager.cacheImages(breakdownId, images, imageIds);
export const isCacheValid = (breakdownId) => imageCacheManager.isCacheValid(breakdownId);
export const compareImageIds = (cachedIds, freshIds) => imageCacheManager.compareImageIds(cachedIds, freshIds);
export const clearExpiredCache = () => imageCacheManager.clearExpiredCache();
export const clearAllCache = () => imageCacheManager.clearAllCache();
export const getCacheStats = () => imageCacheManager.getCacheStats();
